import {
  getEnvironmentKind,
  getIsLocalEnvironment,
} from './getEnvironmentKind';
import { getUtiqHost } from './getUtiqHost';
import { consoleLogger } from './logging/consoleLogger';

/**
  Generates a path to be used for loading a utiq-specific script, either JS or CSS.
  The way we generate these paths differes between local, test, and prod envs.

  @param scriptName unqualified script name, including suffix
  @returns the fully qualified URL for the script, including origin and path
*/
function standardScriptResolution(scriptName: string): string {
  const utiqHost = getUtiqHost();

  consoleLogger.logTrace(
    `generating path for ${scriptName} (${JSON.stringify({
      isLocalEnvironment: getIsLocalEnvironment(),
      utiqHost,
    })})`,
  );

  const resolved = (() => {
    switch (getEnvironmentKind()) {
      case 'UAT': {
        const url = new URL(utiqHost);

        url.pathname = '/' + scriptName;

        return url.toString();
      }

      case 'staging': {
        const url = new URL(window.location.toString());

        url.pathname =
          url.pathname.split('/').slice(0, 3).join('/') + '/' + scriptName;

        return url.toString();
      }

      case 'local': {
        return `${window.location.origin}/${scriptName}`;
      }

      default: {
        return `${utiqHost}/${scriptName}`;
      }
    }
  })();

  consoleLogger.logTrace(
    `generating path for ${scriptName} (${JSON.stringify({
      isLocalEnvironment: getIsLocalEnvironment(),
      utiqHost,
    })}) = ${resolved}`,
  );

  return resolved;
}

export function getUtiqConsentManagerScriptPath(): string {
  if (window.Utiq?.config?.consentManagerOrigin) {
    return [
      window.Utiq?.config?.consentManagerOrigin.replace(/\/+$/, ''),
      'utiqConsentManager.js',
    ].join('/');
  }

  return standardScriptResolution('utiqConsentManager.js');
}

export function getUtiqConsentManagerStylePath(): string {
  if (window.Utiq?.config?.consentManagerOrigin) {
    return [
      window.Utiq?.config?.consentManagerOrigin.replace(/\/+$/, ''),
      'utiqConsentManager.css',
    ].join('/');
  }

  return standardScriptResolution('utiqConsentManager.css');
}

export function getModalPopupScriptPath(): string {
  return standardScriptResolution('modalPopup.js');
}

export function getModalPopupStylePath(): string {
  return standardScriptResolution('modalPopup.css');
}

export function getRevocationPopupScriptPath(): string {
  return standardScriptResolution('revocationPopup.js');
}
